import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { IconPencil, IconSend, IconX } from "@/components/icons";
import { FieldRenderer } from "@/components/forms/FieldRenderer";
import { SubmissionAnswersList } from "@/components/forms/SubmissionAnswersList";
import type { FormFieldRef } from "@/components/forms/types";
import { submitForm } from "@/api/forms";
import { ApiError } from "@/api/client";
import { isDisplayFieldType } from "@/lib/formBuilder/apiFieldTypes";
import { buildSubmitPayload } from "@/lib/forms/buildSubmitPayload";
import {
  chatbotAnswerText,
  chatbotQuestion,
  initialChatHistory,
  isChatbotWidgetField,
  nextChatField,
} from "@/lib/forms/chatbotFields";
import { serializeFieldDescription } from "@/lib/forms/fieldDescription";
import { buildExternalSubmissionView } from "@/lib/forms/externalSubmissionView";
import { readShowSubmissionAfterSubmit } from "@/lib/forms/formMeta";
import { submissionFromSubmitResponse } from "@/lib/forms/normalizeSubmission";
import {
  clearFormResponseDraft,
  loadFormResponseDraft,
  saveFormResponseDraft,
} from "@/lib/forms/responseDraft";
import { formatSubmitError } from "@/lib/http/formatSubmitError";
import { useFormVisibility } from "@/hooks/useFormVisibility";
import {
  collectFieldsInFormOrder,
  findFieldSection,
  isFieldVisible,
  pruneHiddenFieldValues,
} from "@/lib/forms/visibility";
import type { ValuesByName } from "@/types/visibility";
import type { FieldDto } from "@/types/field";
import type { SectionDto } from "@/types/section";
import type { FormFieldAnswer } from "@/types/submit";
import type { FormSubmissionDto } from "@/types/submission";

const NEXT_QUESTION_DELAY_MS = 260;

function BotBubble({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "mr-10 max-w-[92%] overflow-visible rounded-2xl rounded-bl-md bg-slate-100 px-3.5 py-2.5 font-onest text-sm leading-relaxed text-slate-800",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
}

function UserBubble({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "min-w-0 max-w-[calc(100%-2.25rem)] break-words rounded-2xl rounded-br-md bg-brand-500 px-3.5 py-2.5 font-onest text-sm leading-relaxed text-white",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
}

function TypingBubble() {
  return (
    <BotBubble className="form-chat-enter">
      <span className="inline-flex items-center gap-1 py-0.5" aria-hidden>
        <span className="form-chat-typing-dot size-1.5 rounded-full bg-slate-400" />
        <span className="form-chat-typing-dot size-1.5 rounded-full bg-slate-400" />
        <span className="form-chat-typing-dot size-1.5 rounded-full bg-slate-400" />
      </span>
    </BotBubble>
  );
}

function SectionChip({ label }: { label: string }) {
  return (
    <p className="text-center font-onest text-[11px] font-medium uppercase tracking-wide text-slate-400">
      {label}
    </p>
  );
}

function RequiredMark() {
  return (
    <>
      <span className="text-red-500" aria-hidden="true">
        {" "}
        *
      </span>
      <span className="sr-only"> (обязательный вопрос)</span>
    </>
  );
}

function BotQuestion({
  field,
  className = "",
}: {
  field: FieldDto;
  className?: string;
}) {
  const question = chatbotQuestion(field);
  if (!question) return null;
  const hint = serializeFieldDescription(field.description);
  const showHint = Boolean(hint && hint !== question);
  return (
    <BotBubble className={className}>
      <span>{question}</span>
      {field.required ? <RequiredMark /> : null}
      {showHint ? (
        <p className="mt-1 font-onest text-xs font-normal leading-snug text-slate-500">
          {hint}
        </p>
      ) : null}
    </BotBubble>
  );
}

export function ChatbotForm({
  sections,
  rootFields = [],
  description = "",
  submitDisabled = false,
  showSectionLabels = true,
  showSubmissionAfterSubmit = false,
  cityNames,
  formId,
  previewMode = false,
}: {
  sections: SectionDto[];
  rootFields?: FieldDto[];
  description?: string;
  submitDisabled?: boolean;
  showSectionLabels?: boolean;
  showSubmissionAfterSubmit?: boolean;
  cityNames?: Record<string, string>;
  formId?: string;
  previewMode?: boolean;
}) {
  const navigate = useNavigate();
  const publicFormId = formId?.trim() ?? "";
  const orderedFields = useMemo(
    () => collectFieldsInFormOrder(sections, rootFields),
    [rootFields, sections],
  );
  const restoredDraft = useMemo(
    () =>
      previewMode
        ? null
        : loadFormResponseDraft(
            publicFormId,
            orderedFields.map((field) => field.name),
          ),
    [orderedFields, previewMode, publicFormId],
  );
  const { valuesByName, visibleByName, setFieldValue } = useFormVisibility(
    orderedFields,
    restoredDraft?.values,
  );
  const [history, setHistory] = useState<string[]>(() =>
    initialChatHistory(orderedFields, restoredDraft?.values ?? {}),
  );
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingPreview, setEditingPreview] = useState<string | null>(null);
  const [isSending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [completedSubmission, setCompletedSubmission] =
    useState<FormSubmissionDto | null>(null);
  const fieldRef = useRef<FormFieldRef>(null);
  const labeledByName = useRef(new Map<string, { label: string; value: string }>());
  const listRef = useRef<HTMLDivElement>(null);
  const submittedRef = useRef(false);
  const historyLenRef = useRef(history.length);
  const previousQuestionName = useRef<string | null>(null);
  const skipQuestionDelay = useRef(true);
  const [questionReady, setQuestionReady] = useState(true);
  const [enteringAnswerName, setEnteringAnswerName] = useState<string | null>(
    null,
  );

  const currentField = nextChatField(orderedFields, valuesByName, history);
  const composerField = editingName
    ? (orderedFields.find((field) => field.name === editingName) ?? currentField)
    : currentField;
  const editingQuestion = composerField
    ? chatbotQuestion(composerField) || composerField.label || composerField.name
    : "";
  const isLastAnswerable = useMemo(() => {
    if (editingName) {
      return nextChatField(orderedFields, valuesByName, history) == null;
    }
    if (!currentField || isDisplayFieldType(currentField.type)) return false;
    const after = [...history, currentField.name];
    return nextChatField(orderedFields, valuesByName, after) == null;
  }, [currentField, editingName, history, orderedFields, valuesByName]);

  useEffect(() => {
    if (previewMode || completedSubmission) return;
    saveFormResponseDraft(publicFormId, {
      values: valuesByName,
      currentStep: history.length,
    });
  }, [
    completedSubmission,
    history.length,
    previewMode,
    publicFormId,
    valuesByName,
  ]);

  useEffect(() => {
    if (editingName) {
      document
        .getElementById(`chat-msg-${editingName}`)
        ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      return;
    }
    listRef.current?.lastElementChild?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  }, [currentField?.name, editingName, history.length, questionReady]);

  useEffect(() => {
    if (history.length <= historyLenRef.current) {
      historyLenRef.current = history.length;
      return;
    }
    const newest = history[history.length - 1] ?? null;
    historyLenRef.current = history.length;
    setEnteringAnswerName(newest);
    const timer = window.setTimeout(() => setEnteringAnswerName(null), 400);
    return () => window.clearTimeout(timer);
  }, [history]);

  useEffect(() => {
    const nextName = currentField?.name ?? null;
    if (editingName) return;
    if (skipQuestionDelay.current) {
      skipQuestionDelay.current = false;
      previousQuestionName.current = nextName;
      setQuestionReady(true);
      return;
    }
    if (nextName === previousQuestionName.current) {
      setQuestionReady(true);
      return;
    }
    previousQuestionName.current = nextName;
    if (!nextName) {
      setQuestionReady(true);
      return;
    }
    setQuestionReady(false);
    const timer = window.setTimeout(
      () => setQuestionReady(true),
      NEXT_QUESTION_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [currentField?.name, editingName]);

  const completedRows = useMemo(
    () =>
      completedSubmission
        ? buildExternalSubmissionView(
            completedSubmission,
            orderedFields,
            cityNames,
          )
        : [],
    [cityNames, completedSubmission, orderedFields],
  );

  const formatAnswer = (field: FieldDto) => {
    const labeled = labeledByName.current.get(field.name);
    const raw = labeled ?? valuesByName[field.name];
    return chatbotAnswerText(field, raw, cityNames);
  };

  const applyLabeledPayload = (payload: FormFieldAnswer[]): FormFieldAnswer[] =>
    payload.map((item) => {
      const field = orderedFields.find((entry) => entry.id === item.field_id);
      const labeled = field ? labeledByName.current.get(field.name) : undefined;
      if (labeled && String(item.value) === labeled.value) {
        return { ...item, value: labeled };
      }
      return item;
    });

  const sendAnswers = async (values: ValuesByName) => {
    if (previewMode || submitDisabled || submittedRef.current) return;
    submittedRef.current = true;
    const payload = applyLabeledPayload(
      buildSubmitPayload(orderedFields, values),
    );
    setSending(true);
    setSubmitError(null);
    try {
      const response = await submitForm(publicFormId, { data: payload });
      clearFormResponseDraft(publicFormId);
      const showAnswers =
        readShowSubmissionAfterSubmit(response) ?? showSubmissionAfterSubmit;
      if (showAnswers) {
        setCompletedSubmission(submissionFromSubmitResponse(response, payload));
        return;
      }
      navigate(`/success?form-id=${formId}`);
    } catch (err) {
      submittedRef.current = false;
      if (import.meta.env.DEV && err instanceof ApiError) {
        console.error(
          "[chmforms] submit failed",
          err.status,
          err.errors ?? err.message,
        );
      }
      setSubmitError(formatSubmitError(err));
    } finally {
      setSending(false);
    }
  };

  const confirmCurrent = async () => {
    if (!composerField || isSending) return;
    if (submitDisabled && !previewMode) return;
    const handle = fieldRef.current;
    if (handle && !handle.validate()) {
      setFieldError("Заполните поле");
      return;
    }
    setFieldError(null);
    const fieldName = composerField.name;
    if (handle?.labeledChoice) {
      labeledByName.current.set(fieldName, handle.labeledChoice);
    } else {
      labeledByName.current.delete(fieldName);
    }
    const nextValues = pruneHiddenFieldValues(orderedFields, {
      ...valuesByName,
      [fieldName]: handle?.value ?? valuesByName[fieldName] ?? "",
    });
    if (handle && !isDisplayFieldType(composerField.type)) {
      setFieldValue(fieldName, handle.value);
    }
    const nextHistory = editingName
      ? history.filter((name) => {
          const field = orderedFields.find((item) => item.name === name);
          return field ? isFieldVisible(field, nextValues) : false;
        })
      : [...history, fieldName];
    setEditingName(null);
    setEditingPreview(null);
    setHistory(nextHistory);
    const upcoming = nextChatField(orderedFields, nextValues, nextHistory);
    if (!upcoming) {
      await sendAnswers(nextValues);
    }
  };

  const editAnswer = (name: string) => {
    if (isSending) return;
    const field = orderedFields.find((item) => item.name === name);
    setEditingPreview(field ? formatAnswer(field) : null);
    setEditingName(name);
    setFieldError(null);
    setSubmitError(null);
    submittedRef.current = false;
  };

  const cancelEdit = () => {
    setEditingName(null);
    setEditingPreview(null);
    setFieldError(null);
  };

  if (completedSubmission) {
    return (
      <div className="space-y-5">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3.5 sm:px-5">
          <p className="font-onest text-sm font-medium text-emerald-900 sm:text-base">
            Спасибо, ваше сообщение отправлено.
          </p>
        </div>
        <SubmissionAnswersList rows={completedRows} />
        {formId ? (
          <a
            href={`/forms/${formId}`}
            className="inline-flex w-full items-center justify-center rounded-xl bg-brand-500 px-6 py-2.5 font-onest text-[15px] font-medium text-white shadow-md shadow-brand-500/25 transition-colors hover:bg-brand-600 sm:w-auto"
          >
            Заполнить форму ещё раз
          </a>
        ) : null}
      </div>
    );
  }

  let lastSectionKey = "";
  const intro = description.trim();

  return (
    <div className="flex flex-col gap-3">
      <div ref={listRef} className="flex flex-col gap-3">
        {intro ? <BotBubble>{intro}</BotBubble> : null}

        {history.map((name) => {
          const field = orderedFields.find((item) => item.name === name);
          if (!field || visibleByName[field.name] === false) return null;
          const section = findFieldSection(sections, field.name);
          const sectionKey = section ? String(section.id ?? section.label) : "";
          const sectionLabel = (section?.label ?? "").trim();
          const showSection =
            showSectionLabels &&
            sectionLabel &&
            sectionKey !== lastSectionKey;
          if (showSection) lastSectionKey = sectionKey;
          const display = isDisplayFieldType(field.type);
          return (
            <div key={`done-${field.name}`} className="flex flex-col gap-3">
              {showSection ? <SectionChip label={sectionLabel} /> : null}
              <BotQuestion field={field} />
              {display ? (
                <BotBubble>
                  <FieldRenderer
                    field={field}
                    visible
                    hideLabel
                    layout="stacked"
                    valuesByName={valuesByName}
                    onFieldValueChange={setFieldValue}
                    inputRef={() => {}}
                  />
                </BotBubble>
              ) : (
                <div
                  id={`chat-msg-${field.name}`}
                  className="flex w-full items-end justify-end gap-0.5"
                >
                  <button
                    type="button"
                    disabled={isSending}
                    aria-label="Изменить ответ"
                    aria-pressed={editingName === field.name}
                    onClick={() => editAnswer(field.name)}
                    className="mb-0.5 shrink-0 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
                  >
                    <IconPencil size={14} />
                  </button>
                  <UserBubble
                    className={[
                      enteringAnswerName === field.name
                        ? "form-chat-enter-user"
                        : "",
                      editingName === field.name
                        ? "ring-2 ring-white/80 ring-offset-2 ring-offset-brand-500"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {editingName === field.name && editingPreview != null
                      ? editingPreview
                      : formatAnswer(field)}
                  </UserBubble>
                </div>
              )}
            </div>
          );
        })}

        {currentField && !editingName && !questionReady ? (
          <TypingBubble />
        ) : currentField && !editingName ? (
          <div
            key={currentField.name}
            className="form-chat-enter flex flex-col gap-3"
          >
            {(() => {
              const section = findFieldSection(sections, currentField.name);
              const sectionKey = section
                ? String(section.id ?? section.label)
                : "";
              const sectionLabel = (section?.label ?? "").trim();
              const showSection =
                showSectionLabels &&
                sectionLabel &&
                sectionKey !== lastSectionKey;
              const widget = isChatbotWidgetField(currentField.type);
              return (
                <>
                  {showSection ? <SectionChip label={sectionLabel} /> : null}
                  <BotQuestion field={currentField} />
                  {widget ? (
                    <BotBubble className="form-chat-widget">
                      <FieldRenderer
                        key={currentField.name}
                        field={currentField}
                        visible
                        hideLabel
                        layout="stacked"
                        valuesByName={valuesByName}
                        onFieldValueChange={setFieldValue}
                        inputRef={(instance) => {
                          fieldRef.current = instance;
                        }}
                      />
                    </BotBubble>
                  ) : null}
                </>
              );
            })()}
          </div>
        ) : previewMode && !currentField ? (
          <BotBubble className="form-chat-enter">
            На этом вопросы закончились.
          </BotBubble>
        ) : null}
      </div>

      {fieldError ? (
        <p className="font-onest text-sm text-red-600" role="alert">
          {fieldError}
        </p>
      ) : null}
      {submitError ? (
        <div
          className="form-alert-error rounded-xl border border-red-200 bg-red-50 px-4 py-3"
          role="alert"
        >
          <p className="font-onest text-sm text-red-700">{submitError}</p>
        </div>
      ) : null}

      {composerField &&
      (editingName || questionReady) &&
      !isChatbotWidgetField(composerField.type) ? (
        <form
          className={[
            "flex flex-col",
            editingName ? "" : "form-chat-enter",
          ]
            .filter(Boolean)
            .join(" ")}
          onSubmit={(event) => {
            event.preventDefault();
            void confirmCurrent();
          }}
        >
          {editingName ? (
            <div className="flex items-center gap-2 rounded-t-[20px] border border-b-0 border-slate-200 bg-slate-50 px-3 py-2">
              <span className="h-8 w-0.5 shrink-0 rounded-full bg-brand-500" />
              <div className="min-w-0 flex-1">
                <p className="font-onest text-[11px] font-medium text-brand-600">
                  Редактирование
                </p>
                <p className="truncate font-onest text-sm text-slate-800">
                  {editingQuestion}
                  {composerField?.required ? <RequiredMark /> : null}
                </p>
                {editingPreview ? (
                  <p className="truncate font-onest text-xs text-slate-500">
                    {editingPreview}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                aria-label="Отменить редактирование"
                onClick={cancelEdit}
                className="shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <IconX size={16} />
              </button>
            </div>
          ) : null}
          <div
            className={[
              "form-chat-composer flex min-w-0 flex-1 items-end overflow-visible border border-slate-200 bg-slate-50 py-1 pl-3 pr-1",
              editingName ? "rounded-b-[24px] rounded-t-none" : "rounded-[24px]",
            ].join(" ")}
          >
            <div className="min-w-0 flex-1">
              <FieldRenderer
                key={composerField.name}
                field={composerField}
                visible
                hideLabel
                layout="stacked"
                valuesByName={valuesByName}
                onFieldValueChange={setFieldValue}
                inputRef={(instance) => {
                  fieldRef.current = instance;
                }}
              />
            </div>
            {submitDisabled ? null : (
              <button
                type="submit"
                disabled={isSending}
                aria-label={
                  isLastAnswerable ? "Отправить" : "Отправить сообщение"
                }
                className="form-submit-btn form-chat-send inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <IconSend size={16} />
              </button>
            )}
          </div>
        </form>
      ) : composerField &&
        (editingName || questionReady) &&
        isChatbotWidgetField(composerField.type) ? (
        <div className="flex flex-col gap-2">
          {editingName ? (
            <div className="flex items-center gap-2 rounded-[20px] border border-slate-200 bg-slate-50 px-3 py-2">
              <span className="h-8 w-0.5 shrink-0 rounded-full bg-brand-500" />
              <div className="min-w-0 flex-1">
                <p className="font-onest text-[11px] font-medium text-brand-600">
                  Редактирование
                </p>
                <p className="truncate font-onest text-sm text-slate-800">
                  {editingQuestion}
                </p>
              </div>
              <button
                type="button"
                aria-label="Отменить редактирование"
                onClick={cancelEdit}
                className="shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <IconX size={16} />
              </button>
            </div>
          ) : null}
        <div className="flex justify-end">
          {submitDisabled ? (
            <p className="font-onest text-sm text-ink-muted">
              Приём ответов закрыт
            </p>
          ) : (
            <button
              type="button"
              disabled={isSending}
              onClick={() => void confirmCurrent()}
              aria-label="Отправить сообщение"
              className="form-submit-btn form-chat-send inline-flex size-11 items-center justify-center rounded-full bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50"
            >
              <IconSend size={18} />
            </button>
          )}
        </div>
        </div>
      ) : null}

      {submitDisabled &&
      composerField &&
      (editingName || questionReady) &&
      !isChatbotWidgetField(composerField.type) ? (
        <p className="text-right font-onest text-sm text-ink-muted">
          Приём ответов закрыт
        </p>
      ) : null}
    </div>
  );
}
